'use client'

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { 
  Users, 
  TrendingUp, 
  AlertTriangle, 
  Calendar, 
  MessageCircle, 
  Download,
  Filter,
  Search,
  MoreHorizontal,
  Plus,
  BarChart3,
  Eye,
  Award,
  Target,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react'
import {
  TeamOverviewWidget,
  TeamMemberCard,
  PerformanceAlertCard,
  PerformanceLevelBadge,
  TeamMemberDetailsModal
} from '../../components/performance'
import { 
  generateTeamData, 
  generatePerformanceAlerts, 
  calculateTeamStats
} from '../../lib/performance-utils'
import { MetricCard } from '../../components/MetricCard'
import { usePageTitle } from '@/stores/page-title-store'

function TeamEvaluationContent() {
  const { user } = useAuth()
  usePageTitle('Avaliação da Equipe')
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('current')
  const [filterBy, setFilterBy] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedMemberForModal, setSelectedMemberForModal] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [teamStats, setTeamStats] = useState<any>(null)
  const [alerts, setAlerts] = useState<any[]>([])

  // Load team data asynchronously
  useEffect(() => {
    const loadTeamData = async () => {
      if (!user?.email) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const teamData = await generateTeamData(user.email)
        setTeamMembers(teamData)

        // Calculate derived data
        const stats = calculateTeamStats(teamData)
        const performanceAlerts = generatePerformanceAlerts(teamData)

        setTeamStats(stats)
        setAlerts(performanceAlerts)
      } catch (error) {
        console.error('Error loading team data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadTeamData()
  }, [user?.email])

  // Modal handlers
  const openMemberDetailsModal = (member: any) => {
    setSelectedMemberForModal(member)
    setIsModalOpen(true)
  }

  const closeMemberDetailsModal = () => {
    setIsModalOpen(false)
    setSelectedMemberForModal(null)
  }

  // Filter team members
  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.role.toLowerCase().includes(searchTerm.toLowerCase())
    
    if (!matchesSearch) return false
    
    switch (filterBy) {
      case 'high-performance':
        return member.score >= 90
      case 'needs-attention':
        return member.score < 70
      case 'pending-review':
        return parseInt(member.lastUpdated) > 30
      default:
        return true
    }
  })

  // Handle member actions
  const handleMemberAction = (action: string, memberId: string) => {
    if (action === 'view_details') {
      const member = teamMembers.find(m => m.id === memberId)
      if (member) {
        openMemberDetailsModal(member)
      }
      return
    }
    
    // In a real app, this would trigger actual actions like scheduling, feedback, etc.
  }

  // Handle alert actions
  const handleResolveAlert = (alertId: string) => {
    // In a real app, this would mark the alert as resolved
  }

  const handleViewAlertDetails = (alertId: string) => {
    // In a real app, this would open a modal with alert details
  }

  const handleScheduleAction = (memberId: string, action: string) => {
    // In a real app, this would open scheduling interface
  }

  if (!user) return null

  if (loading || !teamStats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-brand-primary border-t-transparent"></div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-8">
        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-select bg-brand-dark border-brand-gray text-white rounded-monumenta"
          >
            <option value="current">Período Atual</option>
            <option value="q3-2024">Q3 2024</option>
            <option value="q2-2024">Q2 2024</option>
            <option value="q1-2024">Q1 2024</option>
          </select>

          <button className="btn-ghost">
            <Download size={16} className="mr-2" />
            Exportar
          </button>

          <button className="btn-primary">
            <Plus size={16} className="mr-2" />
            Nova Avaliação
          </button>
        </div>

        {/* Main Content */}
        
        {/* Team Overview */}
        <div className="mb-8">
          <TeamOverviewWidget
            stats={teamStats}
            teamName="Equipe de Tecnologia"
            period="Outubro 2024"
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Score Médio"
            value={teamStats.averageScore}
            change="+3.2"
            icon={TrendingUp}
            trend="up"
          />
          <MetricCard
            title="Alta Performance"
            value={teamStats.distributionByLevel.high_performance || 0}
            icon={Users}
            trend="neutral"
          />
          <MetricCard
            title="Alertas Ativos"
            value={alerts.filter(a => !a.resolved).length}
            change={alerts.filter(a => a.severity === 'critical').length > 0 ? 'Crítico' : 'Normal'}
            icon={AlertTriangle}
            trend={alerts.filter(a => a.severity === 'critical').length > 0 ? 'down' : 'neutral'}
          />
          <MetricCard
            title="1:1s este Mês"
            value={`${teamStats.completedOneOnOnes}/${teamStats.activeMembers}`}
            change={`${Math.round((teamStats.completedOneOnOnes / teamStats.activeMembers) * 100)}%`}
            icon={Calendar}
            trend="up"
          />
        </div>

        <div className="space-y-8">
          
          {/* Team Members */}
          <div className="space-y-6">
            
            {/* Filters and Search */}
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Buscar por nome ou função..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-input pl-10 pr-4 py-2 w-64 bg-brand-dark border-brand-gray text-white rounded-monumenta"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray" size={16} />
                  </div>
                  
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                    className="form-select bg-brand-dark border-brand-gray text-white rounded-monumenta"
                  >
                    <option value="all">Todos os membros</option>
                    <option value="high-performance">Alta Performance</option>
                    <option value="needs-attention">Precisam de atenção</option>
                    <option value="pending-review">Avaliação pendente</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`btn-icon ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    <BarChart3 size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`btn-icon ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    <Eye size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Team Members Grid */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-display font-semibold text-white">
                  Membros da Equipe ({filteredMembers.length})
                </h2>
                {filterBy !== 'all' && (
                  <PerformanceLevelBadge 
                    level={filterBy === 'high-performance' ? 'Alta Performance' : 
                           filterBy === 'needs-attention' ? 'Atenção' : 'Pendente'}
                    score={filterBy === 'high-performance' ? 95 : 
                           filterBy === 'needs-attention' ? 65 : 50}
                    size="sm"
                  />
                )}
              </div>
              
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
                {filteredMembers.map((member) => (
                  <TeamMemberCard
                    key={member.id}
                    {...member}
                    onActionClick={handleMemberAction}
                  />
                ))}
              </div>
              
              {filteredMembers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="text-brand-gray mx-auto mb-4" size={48} />
                  <h3 className="text-lg font-display font-semibold text-white mb-2">
                    Nenhum membro encontrado
                  </h3>
                  <p className="text-brand-gray">
                    Tente ajustar os filtros ou termo de busca
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Alerts & Actions */}
          {/* 
          <div className="xl:col-span-4 space-y-6">
            
            {/* Performance Alerts */}
            {/*
            <PerformanceAlertCard
              alerts={alerts}
              onResolveAlert={handleResolveAlert}
              onViewDetails={handleViewAlertDetails}
              onScheduleAction={handleScheduleAction}
              showResolved={false}
            />

            {/* Team Actions */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="font-display font-semibold text-brand-primary mb-4">
                Ações da Equipe
              </h3>
              
              <div className="space-y-3">
                <button className="w-full btn-ghost text-left flex items-center space-x-3 p-3 rounded-monumenta">
                  <Calendar className="text-brand-blue" size={20} />
                  <div>
                    <div className="font-display font-medium text-white">Agendar 1:1s</div>
                    <div className="text-xs text-brand-gray">Com toda a equipe</div>
                  </div>
                </button>
                
                <button className="w-full btn-ghost text-left flex items-center space-x-3 p-3 rounded-monumenta">
                  <MessageCircle className="text-brand-primary" size={20} />
                  <div>
                    <div className="font-display font-medium text-white">Feedback em Grupo</div>
                    <div className="text-xs text-brand-gray">Sessão de retrospectiva</div>
                  </div>
                </button>
                
                <button className="w-full btn-ghost text-left flex items-center space-x-3 p-3 rounded-monumenta">
                  <TrendingUp className="text-success" size={20} />
                  <div>
                    <div className="font-display font-medium text-white">Relatório Performance</div>
                    <div className="text-xs text-brand-gray">Para liderança</div>
                  </div>
                </button>
                
                <button className="w-full btn-ghost text-left flex items-center space-x-3 p-3 rounded-monumenta">
                  <Users className="text-warning" size={20} />
                  <div>
                    <div className="font-display font-medium text-white">Plano de Desenvolvimento</div>
                    <div className="text-xs text-brand-gray">Para equipe</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Performance Distribution */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="font-display font-semibold text-brand-primary mb-4">
                Distribuição de Performance
              </h3>
              
              <div className="space-y-4">
                {[
                  { level: 'Alta Performance', count: teamStats.distributionByLevel.high || 0, color: 'bg-success', textColor: 'text-success', icon: Award, percentage: ((teamStats.distributionByLevel.high || 0) / teamStats.totalMembers) * 100 },
                  { level: 'Potencial', count: teamStats.distributionByLevel.potential || 0, color: 'bg-brand-primary', textColor: 'text-brand-primary', icon: Zap, percentage: ((teamStats.distributionByLevel.potential || 0) / teamStats.totalMembers) * 100 },
                  { level: 'Crescimento', count: teamStats.distributionByLevel.growth || 0, color: 'bg-brand-blue', textColor: 'text-brand-blue', icon: TrendingUp, percentage: ((teamStats.distributionByLevel.growth || 0) / teamStats.totalMembers) * 100 },
                  { level: 'Cumpre Função', count: teamStats.distributionByLevel.fulfills || 0, color: 'bg-info', textColor: 'text-info', icon: CheckCircle, percentage: ((teamStats.distributionByLevel.fulfills || 0) / teamStats.totalMembers) * 100 },
                  { level: 'Sob Análise', count: teamStats.distributionByLevel.analysis || 0, color: 'bg-warning', textColor: 'text-warning', icon: Clock, percentage: ((teamStats.distributionByLevel.analysis || 0) / teamStats.totalMembers) * 100 }
                ].filter(item => item.count > 0).map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <item.icon className={`${item.textColor} flex-shrink-0`} size={16} />
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-white">{item.level}</span>
                        <span className="text-sm text-brand-gray">{item.count}</span>
                      </div>
                      <div className="w-full bg-brand-dark rounded-full h-1">
                        <div 
                          className={`h-1 rounded-full ${item.color}`}
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Team Activity */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="font-display font-semibold text-brand-primary mb-4">
                Atividade Recente
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-success rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">Maria Santos atingiu meta</p>
                    <p className="text-xs text-brand-gray">Há 1 hora</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">1:1 agendado com João</p>
                    <p className="text-xs text-brand-gray">Há 3 horas</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-warning rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">Alert: Pedro precisa de suporte</p>
                    <p className="text-xs text-brand-gray">Há 6 horas</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">Feedback enviado para equipe</p>
                    <p className="text-xs text-brand-gray">Ontem</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          */}

          {/* Performance Distribution - moved to main area */}
          <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
            <h3 className="font-display font-semibold text-brand-primary mb-4">
              Distribuição de Performance
            </h3>
            
            <div className="space-y-4">
              {[
                { level: 'Alta Performance', count: teamStats.distributionByLevel.high || 0, color: 'bg-success', textColor: 'text-success', icon: Award, percentage: ((teamStats.distributionByLevel.high || 0) / teamStats.totalMembers) * 100 },
                { level: 'Potencial', count: teamStats.distributionByLevel.potential || 0, color: 'bg-brand-primary', textColor: 'text-brand-primary', icon: Zap, percentage: ((teamStats.distributionByLevel.potential || 0) / teamStats.totalMembers) * 100 },
                { level: 'Crescimento', count: teamStats.distributionByLevel.growth || 0, color: 'bg-brand-blue', textColor: 'text-brand-blue', icon: TrendingUp, percentage: ((teamStats.distributionByLevel.growth || 0) / teamStats.totalMembers) * 100 },
                { level: 'Cumpre Função', count: teamStats.distributionByLevel.fulfills || 0, color: 'bg-info', textColor: 'text-info', icon: CheckCircle, percentage: ((teamStats.distributionByLevel.fulfills || 0) / teamStats.totalMembers) * 100 },
                { level: 'Sob Análise', count: teamStats.distributionByLevel.analysis || 0, color: 'bg-warning', textColor: 'text-warning', icon: Clock, percentage: ((teamStats.distributionByLevel.analysis || 0) / teamStats.totalMembers) * 100 }
              ].filter(item => item.count > 0).map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <item.icon className={`${item.textColor} flex-shrink-0`} size={16} />
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-white">{item.level}</span>
                      <span className="text-sm text-brand-gray">{item.count}</span>
                    </div>
                    <div className="w-full bg-brand-dark rounded-full h-1">
                      <div 
                        className={`h-1 rounded-full ${item.color}`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Team Member Details Modal */}
      <TeamMemberDetailsModal
        member={selectedMemberForModal}
        isOpen={isModalOpen}
        onClose={closeMemberDetailsModal}
      />
    </>
  )
}

export default function TeamEvaluationPage() {
  return (
    <ProtectedRoute>
      <TeamEvaluationContent />
    </ProtectedRoute>
  )
}
